import { CardFundingType } from '@weave/schema-gen-ts/dist/schemas/payments/messages/values/values.pb';
import { FundingTypeDisplayValues, PaymentOrigin, PaymentType } from '@frontend/api-invoices';
import { formatCentsToCurrency } from '@frontend/currency';
import { formatDate } from '@frontend/date';
import { TFunction } from '@frontend/i18n';
import { CardBrand } from '@frontend/payments-card-brand';
import { StatusChip } from '@frontend/payments-status-chip';
import { theme } from '@frontend/theme';
import { CopyableText, TableColumnConfig } from '@frontend/design-system';
import { PaymentRow } from '../../types/invoice';
import { getPaymentMethodBrand } from '../../utils/helpers';
import { getPaymentOriginLabel } from './get-payment-origin-label';

/**
 * Outcome: Default empty user value should be "N/A" for:
 * - text to pay (online bill pay flow w/ invoice id)
 * - online bill pay (online bill pay flow w/o invoice id)
 */
export const neverIncludedOrigins: PaymentOrigin[] = [PaymentOrigin.TextToPay, PaymentOrigin.OnlineBillPay];
export const renderPaymentMethod = (paymentType: PaymentType) => {
  const label = paymentType === PaymentType.CardPresent ? 'Card' : paymentType ?? '';
  return label.charAt(0).toUpperCase() + label.slice(1).toLowerCase();
};
export const renderCardType = (fundingType: CardFundingType) =>
  fundingType ? FundingTypeDisplayValues[fundingType] : '';

export type PaymentRequestDetailsGenerateColumnsOptions = {
  hasSurchargesEnabled?: boolean;
};
export const generateColumns = (
  t: TFunction,
  { hasSurchargesEnabled = false }: PaymentRequestDetailsGenerateColumnsOptions = {}
): TableColumnConfig<PaymentRow>[] => [
  {
    id: 'date',
    Header: t('Date', { ns: 'payments' }),
    accessor: (payment) => formatDate(payment.date, 'MMM D, YYYY'),
    disableSortBy: true,
    width: 120,
  },
  {
    id: 'time',
    Header: t('Time', { ns: 'payments' }),
    accessor: (payment) => formatDate(payment.date, 'h:mm A'),
    disableSortBy: true,
    width: 100,
  },
  {
    id: 'amount',
    Header: t('Amount', { ns: 'payments' }),
    accessor: (payment) => formatCentsToCurrency(payment.amount),
    disableSortBy: true,
    width: 100,
  },
  {
    id: 'surcharge',
    Header: t('Surcharge Amount', { ns: 'payments' }),
    accessor: (payment) => formatCentsToCurrency(payment.appliedSurchargeAmount ?? 0),
    disableSortBy: true,
    omit: !hasSurchargesEnabled,
    width: 160,
  },
  {
    id: 'origin',
    Header: t('Collection Method', { ns: 'payments' }),
    accessor: (payment) => getPaymentOriginLabel({ payment, t }),
    disableSortBy: true,
    width: 170,
  },
  {
    id: 'pay_method',
    Header: t('Payment Method', { ns: 'payments' }),
    accessor: (payment) => renderPaymentMethod(payment.paymentType),
    disableSortBy: true,
    width: 170,
  },
  {
    id: 'card_type',
    Header: t('Card Type', { ns: 'payments' }),
    accessor: (payment) => renderCardType(payment.paymentDetails?.fundingType),
    disableSortBy: true,
    width: 120,
  },
  {
    id: 'method',
    Header: t('Card Brand', { ns: 'payments' }),
    accessor: (payment) => payment,
    cellRenderer: (payment: PaymentRow) => {
      return (
        <CardBrand
          brand={getPaymentMethodBrand({
            paymentType: payment?.paymentType,
            brand: payment?.paymentDetails.brand,
            bankName: payment?.paymentDetails.bankName,
            processorType: payment?.processorType,
          })}
          last4={payment?.paymentDetails.lastFour}
        />
      );
    },
    disableSortBy: true,
    width: 120,
  },
  {
    id: 'confirmation_number',
    Header: t('Confirmation Number', { ns: 'payments' }),
    accessor: (payment) => payment,
    cellRenderer: (payment) =>
      payment.confirmationCode ? (
        <CopyableText
          css={{
            textOverflow: 'ellipsis',
            width: '100%',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            fontSize: theme.font.size.medium,
          }}
        >
          {payment.confirmationCode || '-'}
        </CopyableText>
      ) : (
        '-'
      ),
    disableSortBy: true,
  },
  {
    id: 'user',
    Header: t('Office User', { ns: 'payments' }),
    accessor: ({ user, origin }) => {
      const emptyValue = origin && neverIncludedOrigins.includes(origin) ? t('N/A', { ns: 'payments' }) : '-';
      return user || emptyValue;
    },
    disableSortBy: true,
    width: 140,
  },
  {
    id: 'status',
    Header: t('Status', { ns: 'payments' }),
    accessor: (payment) => payment,
    cellRenderer: (payment) => <StatusChip status={payment?.status} maxWidth={'140'} />,
    disableSortBy: true,
    width: 180,
  },
];
